import Icon from '@atoms/Icon/Icon';
import useAriaLabels from '@utils/hooks/useAriaLabels';

interface Props {
  readonly code: string;
  readonly quantity: number;
  readonly dataTestId?: string;
  readonly onChangeCartItemQuantity: (code: string, delta: number) => void;
  readonly onRemoveItemFromCart: (code: string) => void;
}

const QuantitySelector = ({
  code,
  quantity,
  dataTestId = 'Quantity-Selector',
  onChangeCartItemQuantity,
  onRemoveItemFromCart,
}: Props) => {
  const {
    increaseItemQuantityAriaLabel,
    decreaseItemQuantityAriaLabel,
    removeItemFromCartAriaLabel,
  } = useAriaLabels();

  const handleDecrement = () => {
    if (quantity === 1) {
      onRemoveItemFromCart(code);
    } else {
      onChangeCartItemQuantity(code, -1);
    }
  };

  const handleIncrement = () => {
    onChangeCartItemQuantity(code, 1);
  };

  return (
    <div
      data-testid={dataTestId}
      className="flex w-max items-center justify-center gap-3 rounded-[20px] border border-grey px-3.5 py-2 text-black-100 xl:self-end"
    >
      <button
        className="flex"
        onClick={handleDecrement}
        aria-label={
          quantity === 1
            ? removeItemFromCartAriaLabel
            : decreaseItemQuantityAriaLabel
        }
        data-testid={`${dataTestId}-Decrease-Button`}
      >
        <Icon name={quantity === 1 ? 'bin' : 'minus'} size="1.125rem" />
      </button>

      <span
        className="body-three body-three--bold flex text-black-100"
        aria-live="polite"
        data-testid={`${dataTestId}-Current-Quantity`}
      >
        {quantity}
      </span>

      <button
        className="flex"
        onClick={handleIncrement}
        aria-label={increaseItemQuantityAriaLabel}
        data-testid={`${dataTestId}-Increase-Button`}
      >
        <Icon name="add" size="1.125rem" />
      </button>
    </div>
  );
};

export default QuantitySelector;
